import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";

import FormControl from "@mui/material/FormControl";
import React, {FC, useMemo} from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {ModeType, useDialogModal, useDrawerModal, useGlobalActions} from "@/store/global.c";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, getRealId, setValues} from "@/helper/method";
import {AcceptingVO, useCreateAccepting, useQueryAccepting} from "@/service/accepting.s";
import {AccessTimeOutlined} from '@mui/icons-material';
import {SearchData} from "@/widget/SearchData/SearchData";
import {SearchPlanVO} from "@/service/plan.s";
import {TaskPO} from "@/model/mission";
import {SearchTaskVO, useQueryTask} from "@/service/task.s";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {isNumber} from "lodash";
import {WriteReportForm} from "@/pages/Report/WriteReport";

const validationSchema = z.object({
    planId: z.number({message: "请选择关联目标"}),
    planName: z.ostring().optional(),
    status: z.ostring().default(""),
    statusId: z.number({message: "请选任务状态"}),
    planStageId: z.number({message: "请选择所属目标阶段"}),
    planStage: z.ostring().default(""),
    duration: z.number({message: "请选择预计工时"}),
    priority: z.ostring().optional().default(""),
    taskName: z.string().optional(),
    taskId: z.number().optional(),
    parentId: z.number().optional(),
    finishTime: z.date({message: "请选择实际完成日期"}).nullable().default(null),
    description: z.string().optional(),
    priorityId: z.number({message: "请选择优先级"}),
});

export type WriteAcceptingForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

interface WriteAcceptingProps {
    fromTask?: boolean
    mode: ModeType
}

export const WriteAccepting: FC<WriteAcceptingProps> = ({fromTask = false, mode}) => {
    const allDict = useAllDict()
    const {taskStatus, planStage, taskPriority} = allDict
    const {setToast} = useGlobalActions()
    const {mutateAsync: createAcceptingMutate, isPending: createAcceptingPending} = useCreateAccepting()
    const {onSearch, id: drawerId} = useDrawerModal()
    const {id: dialogId, write} = useDialogModal()
    const id = getRealId(mode, drawerId, dialogId);
    const {setDrawerModal, setDialogModal} = useGlobalActions()
    const {data: taskData} = useQueryTask({id: id}, isNumber(id) && fromTask)
    const {data: acceptingData} = useQueryAccepting({id}, isNumber(id) && !fromTask)
    const task = taskData?.data || {} as TaskPO
    const accepting = acceptingData?.data || {} as AcceptingVO

    const {disable, values} = useMemo(() => {
        if (write == 'edit') {
            return {disable: false, values: accepting}
        } else if (fromTask) {
            return {
                disable: true, values: {
                    taskId: task.id,
                    taskName: task.taskName,
                    planName: task.planName,
                    planId: task.planId,
                    status: task.status,
                    statusId: task.statusId,
                    planStage: task.planStage,
                    planStageId: task.planStageId,
                    duration: task.duration,
                    priority: task.priority,
                    finishTime: task.finishTime,
                    priorityId: task.priorityId,
                }
            }
        }
        return {disable: false, values: {}}
    }, [write, fromTask, accepting])

    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteAcceptingForm>({
        resolver: zodResolver(validationSchema),
        values: values as WriteAcceptingForm,
        defaultValues,
    });

    const onSubmit = async (values: WriteAcceptingForm) => {
        const result = await createAcceptingMutate({...values, taskId: dialogId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setDialogModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    const planId: number = useWatch({
        control,
        name: "planId",
        defaultValue: 0
    })

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <SearchData<WriteAcceptingForm, SearchPlanVO> errMsg={errors?.planId?.message} readOnly={disable} setValue={setValue} tag={5} attr={"planName"}
                                                                  attrId={"planId"}
                                                                  label={"关联目标"}
                                                                  control={control}/>
                </Grid>
                <Grid size={6}>
                    <SearchData<WriteReportForm, SearchTaskVO> errMsg={errors?.taskId?.message} query={{planId}} readOnly={disable || !isNumber(planId)} setValue={setValue} tag={6}
                                                               attr={"taskName"} attrId={"taskId"}
                                                               label={"任务名称"}
                                                               onSelect={(item: SearchTaskVO) => {
                                                                   setValues<Pick<WriteReportForm, "status" | "statusId" | "duration" | "planStageId" | "planStage" | "priority" | "priorityId">>({
                                                                       duration: item?.duration,
                                                                       planStageId: item?.planStageId,
                                                                       planStage: item?.planStage,
                                                                       priority: item?.priority,
                                                                       priorityId: item?.priorityId,
                                                                       status: item?.status,
                                                                       statusId: item?.statusId,
                                                                   }, setValue as any)
                                                               }}
                                                               control={control}/>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>任务状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={taskStatus}
                                value={value as any}
                                readOnly={disable}
                                onChange={(_, option: DictPO | null) => {
                                    setValue("statusId", option!.value)
                                    onChange(option?.label)
                                }}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.statusId}
                                               helperText={errors?.statusId?.message}
                                               variant="outlined"
                                               placeholder={"选择任务状态"} {...params} />
                                )}
                            />
                        }} name={"status"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>优先级</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={taskPriority}
                            value={value as any}
                            readOnly={disable}
                            onChange={(_, option: DictPO | null) => {
                                setValue("priorityId", option!.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.priorityId}
                                           helperText={errors?.priorityId?.message}
                                           variant="outlined"
                                           placeholder={"选择优先级"} {...params} />
                            )}
                        />} name={"priority"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel htmlFor="planStage">所属目标阶段</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            id="planStage"
                            options={planStage}
                            readOnly={disable}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("planStageId", option!.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.planStageId}
                                           helperText={errors?.planStageId?.message}
                                           variant="outlined"
                                           placeholder={"选择目标阶段"} {...params} />
                            )}
                        />} name={"planStage"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="duration">预计工时</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.duration}
                            helperText={errors?.duration?.message}
                            id="duration"
                            value={value}
                            disabled={disable}
                            onChange={event => event.target.value && onChange(parseInt(event.target.value))}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 1000,
                                        maxLength: 3, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <AccessTimeOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="填写预计工时"
                            variant="outlined"
                        />} name={'duration'}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="finishTime">实际完成时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                id='account-settings-date'
                                locale='zhCN'
                                placeholderText={"选择实际完成时间"}
                                customInput={<TextField
                                    error={!!errors?.finishTime}
                                    helperText={errors?.finishTime?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"finishTime"}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel htmlFor="description">验收说明</FormLabel>
                        <TextField
                            id="description"
                            multiline
                            rows={3}
                            {...register("description")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                },
                            }}
                            placeholder="验收说明"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createAcceptingPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
